// src/components/BoardPack/BoardPack.jsx
import { useCallback, useEffect, useRef, useState } from "react";

import { apiCall } from "@/api/apiCall";
import { QueueContext } from "@/context/QueueContext";
import { useExport } from "@/context/ExportContext";
import { useApiQueue } from "@/hooks/useApiQueue";
import { useBoardPackJobs } from "@/hooks/useBoardPackJobs";
import { AlertCircle, ChevronLeft, ChevronRight, Loader2, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { watchReportJob } from "@/lib/reportJobs";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import BoardPackControls from "./common/BoardPackControls";
import FinalizeRequestsPanel from "./FinalizeRequestsPanel";
import { BoardPackLayout } from "./common/BoardPackLayout";
import AreaChartPage from "./pages/area_chart";
import BarChart from "./pages/bar_chart";
import DonutChart from "./pages/donut_chart";
import DonutChart18 from "./pages/donut_chart_18";
import PageOneChartContainer from "./pages/Dotted_line_chart";
import KpiChart12 from "./pages/kpi_chart_12";
import KpiChart12NEW from "./pages/kpi_chart_12_new";
import Kpi_Chart_13 from "./pages/kpi_chart_13";
import KpiChart14 from "./pages/kpi_chart_14";
import KpiChartNEW from "./pages/kpi_chart_new";
import KpiEnforcementTable from "./pages/kpi_enforcement_table";
import LargeTable from "./pages/large_table";
import LastTitle from "./pages/last_title";
import LineBars from "./pages/line_bars";
import Linebarstable from "./pages/line_bars_table";
import MainTable from "./pages/main_table";
import NewChartComponentPage from "./pages/new_chart_component";
import NewChartKPIPage from "./pages/new_chart_kpi";
import NewStackChartPage from "./pages/new_stack_chart";
import Page27AirportEnforcementProfileTable from "./pages/Page27AirportEnforcementProfileTable";
import AlBahaProfile from "./pages/profile_summary_pages/Albahaprofile";
import AlBatha_11 from "./pages/profile_summary_pages/AlBatha_11";
import AlBatha_4 from "./pages/profile_summary_pages/AlBatha_4";
import AlHassaProfile from "./pages/profile_summary_pages/AlHassaProfile";
import BabAlRee_11 from "./pages/profile_summary_pages/BabAlRee_11";
import BabAlRee_4 from "./pages/profile_summary_pages/BabAlRee_4";
import BaljurashiProfile from "./pages/profile_summary_pages/Baljurashiprofile";
import DammamAirport_2 from "./pages/profile_summary_pages/DammamAirport_2";
import DammamCausewayProfile from "./pages/profile_summary_pages/DammamCausewayProfile";
import DigitalCityOffStreetProfile from "./pages/profile_summary_pages/Digitalcityoffstreetprofile";
import DigitalCityOnStreetProfile from "./pages/profile_summary_pages/Digitalcityonstreetprofile";
import DubaiAirportProfile from "./pages/profile_summary_pages/DubaiAirportProfile";
import Gazzaz_11 from "./pages/profile_summary_pages/Gazzaz_11";
import Gazzaz_4 from "./pages/profile_summary_pages/Gazzaz_4";
import JeddahProfileSummary11 from "./pages/profile_summary_pages/jeddah_profile_summary_11";
import JeddahProfileSummary2 from "./pages/profile_summary_pages/jeddah_profile_summary_2";
import JeddahProfile from "./pages/profile_summary_pages/JeddahProfile";
import JedWatch_11 from "./pages/profile_summary_pages/JedWatch_11";
import JedWatch_4 from "./pages/profile_summary_pages/JedWatch_4";
import Jordan_2 from "./pages/profile_summary_pages/Jordan_2";
import JubailProfile from "./pages/profile_summary_pages/Jubailprofile";
import KhamisProfile from "./pages/profile_summary_pages/Khamisprofile";
import LoversMarket_2 from "./pages/profile_summary_pages/LoversMarket_2";
import MadinahHaramProfile from "./pages/profile_summary_pages/MadinahHaramProfile";
import MakkahProfile from "./pages/profile_summary_pages/Makkahprofile";
import MawgifJeddahProfile from "./pages/profile_summary_pages/MawgifJeddahProfile";
import SharjahProfileSummary2 from "./pages/profile_summary_pages/sharjha_profile_summary_2 ";
import TaifProfile from "./pages/profile_summary_pages/Taifprofile";
import MocProfile from "./pages/profile_summary_pages/MocProfile";
import TotalBilal_4 from "./pages/profile_summary_pages/TotalBilal_4";
import ThirdTable from "./pages/third_table";
import TitlePage from "./pages/title_page";
import SlideViewer from "./common/SlideViewer";
import ModernCity from "./pages/profile_summary_pages/ModernCity";
import LoadingComponent from "../common/LoadingComponent";
import CoverPage from "./pages/consolidate_boardpack/cover_page";
import Toc_Page from "./pages/consolidate_boardpack/toc_page";
import PL_Summary_Page from "./pages/consolidate_boardpack/pl_summary_page";
import PL_Report from "./pages/consolidate_boardpack/PL_Report";
import Financial_Table from "./pages/consolidate_boardpack/Financial_Table";
import Mawgif_Group_Table from "./pages/consolidate_boardpack/Mawgif_Group_Table";
import Business_Performance_Page from "./pages/consolidate_boardpack/Business_Performance_Page";
import Bussiness_Performance_By_Country from "./pages/consolidate_boardpack/Bussiness_Performance_By_Country";
import Budget_Actual_Chart_page from "./pages/consolidate_boardpack/Budget_Actual_Chart_page";
import Budget_Actual_Chart_Page_v2 from "./pages/consolidate_boardpack/Budget_Actual_Chart_Page_v2";
import Bridge_Summary_Page from "./pages/consolidate_boardpack/Bridge_Summary_Page";
import CapitalParkingOnStreet from "./pages/profile_summary_pages/CapitalParkingOnStreet";

const MONTH_MAP = {
  Jan: "01",
  Feb: "02",
  Mar: "03",
  Apr: "04",
  May: "05",
  Jun: "06",
  Jul: "07",
  Aug: "08",
  Sep: "09",
  Oct: "10",
  Nov: "11",
  Dec: "12",
};

// renderJob: set only by BoardPackRenderPage, where the server-side render
// worker loads one board pack (no controls, no slide viewer, export styling).
const BoardPack = ({ renderJob = null, onRenderStatus } = {}) => {
  const [selectedYear, setSelectedYear] = useState(renderJob?.year ?? null);
  const [selectedMonth, setSelectedMonth] = useState(renderJob?.month ?? null);
  const [selectedContract, setSelectedContract] = useState(renderJob?.contract ?? null);
  const [selectedBoardPackConfig, setSelectedBoardPackConfig] = useState(null);

  const selectedMonthNumber = MONTH_MAP[selectedMonth] || null;

  const { isExporting } = useExport();

  const [pageViewerOpen, setPageViewerOpen] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const [showScrollButton, setShowScrollButton] = useState(false);
  const [scrollDirection, setScrollDirection] = useState("down");

  const [stablePages, setStablePages] = useState([]);
  const [filtersReady, setFiltersReady] = useState(false);
  const [globalRefreshedPages, setGlobalRefreshedPages] = useState(null);
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);
  const [isConfigLoading, setIsConfigLoading] = useState(true);

  // ✅ LastTitle comment state — lifted up so remount safe hai
  const [lastTitleComment, setLastTitleComment] = useState("");

  // ✅ CFO Comments state — per page_no key se store karo (multiple third_table pages ho sakti hain)
  // { "page_no_1": "comment text", "page_no_2": "comment text" }
  const [cfoCommentsMap, setCfoCommentsMap] = useState({});

  const contentRef = useRef(null);
  const mainContainerRef = useRef(null);

  // The render worker has nobody waiting on screen, so load a few pages at once
  const { enqueue, clearQueue } = useApiQueue(renderJob ? 3 : 1);

  const [isRequestingFinalize, setIsRequestingFinalize] = useState(false);
  // Running / ready background jobs of the selected board pack (not in render mode)
  const boardPackJobs = useBoardPackJobs(
    renderJob ? null : selectedContract,
    selectedYear,
    selectedMonth
  );

  const [goToPageOpen, setGoToPageOpen] = useState(false);
  const [goToPageInput, setGoToPageInput] = useState("");

  const handleGoToPage = () => {
    const num = parseInt(goToPageInput);
    if (!num || num < 1 || num > totalPages) return;

    const sections = contentRef.current?.querySelectorAll("[data-pdf-section]");
    if (sections && sections[num - 1]) {
      sections[num - 1].scrollIntoView({ behavior: "smooth", block: "start" });
    }

    setGoToPageOpen(false);
    setGoToPageInput("");
  };

  // ─────────────────────────────────────────────
  // Derive filtersReady
  // ─────────────────────────────────────────────
  useEffect(() => {
    const ready = !!(selectedYear && selectedMonth && selectedContract && selectedBoardPackConfig);
    setFiltersReady(ready);
  }, [selectedYear, selectedMonth, selectedContract, selectedBoardPackConfig]);

  // ─────────────────────────────────────────────
  // Build stablePages — raw page config store karo, JSX nahi
  // ─────────────────────────────────────────────
  useEffect(() => {
    clearQueue();
    setSelectedBoardPackConfig(null);
  }, [selectedContract]);

  // Render mode: there are no controls to fetch the config, so do it here
  useEffect(() => {
    if (!renderJob) return;
    let cancelled = false;

    apiCall("get", "/boardpack/config/get", { contract: renderJob.contract }, null, false, false)
      .then((response) => {
        if (cancelled) return;
        if (response?.success && response?.data?.config?.pages) {
          setSelectedBoardPackConfig(response.data);
        } else {
          onRenderStatus?.({ error: response?.message || "Board pack configuration not found" });
        }
      })
      .catch((err) => {
        if (!cancelled) onRenderStatus?.({ error: String(err?.message || err) });
      })
      .finally(() => {
        if (!cancelled) setIsConfigLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [renderJob]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPageViewerOpen(false);
    setCurrentPageIndex(0);
    // Render mode gets the comments the user had typed when requesting finalize
    setLastTitleComment(renderJob?.comments?.lastTitleComment ?? "");
    setCfoCommentsMap(renderJob?.comments?.cfoCommentsMap ?? {});
    clearQueue();

    if (!filtersReady || !selectedBoardPackConfig?.config?.pages) {
      setStablePages([]);
      return;
    }

    const pages = selectedBoardPackConfig.config.pages;

    const pageTypes = [
      "title_page",
      "last_title",
      "main_table",
      "third_table",
      "large_table",
      "kpi_enforcement_table",
      "bar_chart",
      "kpi_chart_12",
      "kpi_chart_12_new",
      "new_chart_kpi",
      "kpi_chart_new",
      "new_stack_chart",
      "line_bars",
      "line_bars_table",
      "donut_chart_18",
      "area_chart",
      "kpi_chart_13",
      "kpi_chart_14",
      "new_chart_component",
      "Dotted_Line_Chart",
      "donut_chart",

      // Consolidate BoardPack
      "cover_page",
      "toc_page",
      "pl_summary_page",
      "P&L_Report",
      "Financial_Table",
      "Mawgif_Group_Table",
      "Business_Performance",
      "Bussiness_Performance_By_Country",
      "Budget_Actual_Chart",
      "Budget_Actual_Chart_V2",
      "Bridge_Summary_Page",

      // -- Profiles Pages
      "jeddah_profile_summary02",
      "jeddah_profile_summary10",
      "jeddah_profile_summary_arabic",
      "sharjha_profile_table",
      "profile_summary_airport_jordan",
      "profile_summary_lovers_market",
      "profile_summary_AlBatha_4",
      "profile_summary_AlBatha_11",
      "profile_summary_dammam",
      "profile_summary_bilal",
      "profile_summary_BabAlRee_4",
      "profile_summary_BabAlRee_11",
      "Gazzaz_4",
      "Gazzaz_11",
      "JedWatch_4",
      "JedWatch_11",
      "total_dubai_airport_profile",
      "dubai_airport_t1_profile",
      "dubai_airport_t2_profile",
      "dubai_airport_t3_profile",
      "dubai_airport_cargo_profile",
      "madinah_haram_profile",
      "madinah_haram_parking_profile",
      "madinah_haram_enforcement_profile",
      "taif_on_street_profile",
      "taif_off_street_profile",
      "khamis_on_street_profile",
      "khamis_off_street_profile",
      "jubail_on_street_profile",
      "alhassa_on_street_profile",
      "mawgif_jeddah_on_street_profile",
      "digital_city_off_street_profile",
      "digital_city_on_street_profile",
      "albaha_on_street_profile",
      "albaha_off_street_profile",
      "dammam_causeway_on_street_profile",
      "dammam_causeway_off_street_profile",
      "baljurashi_on_street_profile",
      "baljurashi_off_street_profile",
      "makkah_on_street_profile",
      "makkah_off_street_profile",
      "makkah_misfalah_profile",
      "makkah_jarwal_profile",
      "jeddah_on_street_profile",
      "jeddah_off_street_profile",
      "moc_on_street_profile",
      "modern_city_contract",
      "capital_parking_on_street_profile",
    ];
    const built = pages
      .filter((page) => pageTypes.includes(page?.type))
      .map((page, index) => {
        const key = `${page?.type}-${index}-${selectedYear}-${selectedMonth}-${selectedContract}`;
        return {
          id: key,
          pageNumber: page?.page_no || "-",
          page: page,
        };
      });

    // Render mode can ask for a slice of the pages (big packs are rendered in
    // batches); ids keep their full-pack index so the comments still match
    const range = renderJob?.range;
    const visible = range ? built.slice(range.from, range.to) : built;
    if (renderJob) onRenderStatus?.({ totalPages: built.length, batchPages: visible.length });

    setStablePages(visible);
  }, [
    filtersReady,
    selectedBoardPackConfig,
    selectedYear,
    selectedMonth,
    selectedMonthNumber,
    selectedContract,
  ]);

  // ─────────────────────────────────────────────
  // Keyboard navigation for page viewer
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!pageViewerOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") goToNextPage();
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") goToPreviousPage();
      else if (e.key === "Escape") setPageViewerOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pageViewerOpen, currentPageIndex, stablePages.length]);

  // ─────────────────────────────────────────────
  // Scroll button logic
  // ─────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      const mainElement = document.querySelector("main");
      const currentScroll =
        mainElement?.scrollTop ||
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop;
      const scrollHeight =
        mainElement?.scrollHeight ||
        Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      const clientHeight = mainElement?.clientHeight || window.innerHeight;
      const distanceFromBottom = scrollHeight - currentScroll - clientHeight;

      if (currentScroll < 300) {
        setScrollDirection("down");
        setShowScrollButton(true);
      } else if (distanceFromBottom < 300) {
        setScrollDirection("up");
        setShowScrollButton(true);
      } else {
        setScrollDirection("up");
        setShowScrollButton(currentScroll > 300);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("scroll", handleScroll, { passive: true });
    const mainElement = document.querySelector("main");
    if (mainElement) mainElement.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("scroll", handleScroll);
      if (mainElement) mainElement.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // ─────────────────────────────────────────────
  // Refresh All — single API call, distribute to pages
  // ─────────────────────────────────────────────
  const handleRefreshAll = async () => {
    if (!selectedContract || isRefreshingAll) return;
    setIsRefreshingAll(true);
    try {
      const response = await apiCall("get", "/boardpack/config/get", {
        contract: selectedContract,
      });
      if (response?.success && response?.data?.config?.pages) {
        setGlobalRefreshedPages(response.data.config.pages);
      }
    } catch (err) {
      console.error("Refresh all failed:", err);
    } finally {
      setIsRefreshingAll(false);
    }
  };

  // ─────────────────────────────────────────────
  // Finalize / Export — the server renders PDF + PPTX in the background, so
  // the user can change screen or close the tab right after sending the request
  // ─────────────────────────────────────────────
  const requestBackgroundJob = async (kind, download = null) => {
    if (!selectedContract || !selectedYear || !selectedMonth || isRequestingFinalize) return;
    setIsRequestingFinalize(true);
    try {
      const response = await apiCall(
        "post",
        kind === "export" ? "/reports/request-export" : "/reports/request-finalize",
        {
          contract: selectedContract,
          year: selectedYear,
          month: selectedMonth,
          comments: { lastTitleComment, cfoCommentsMap },
        },
        null,
        false,
        false
      );

      if (response?.success && response?.data?.id) {
        watchReportJob(response.data.id, download);
        const what = kind === "export" ? "Export" : "Finalize";
        toast.success(
          response.already ? `${what} is already in progress` : `${what} request sent`,
          {
            description:
              kind === "export"
                ? "The file is being generated on the server. You can leave this screen — you'll get a download link when it's ready."
                : "The PDF and PowerPoint are being generated on the server. You can leave this screen — you'll be notified when it's done.",
            duration: 6000,
          }
        );
      } else {
        toast.error(response?.message || "Could not send the request");
      }
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Could not send the request");
    } finally {
      setIsRequestingFinalize(false);
    }
  };

  const handleRequestFinalize = () => requestBackgroundJob("finalize");

  const goToNextPage = useCallback(() => {
    setCurrentPageIndex((prev) => Math.min(prev + 1, stablePages.length - 1));
  }, [stablePages.length]);

  const goToPreviousPage = useCallback(() => {
    setCurrentPageIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleScrollClick = () => {
    const mainElement = document.querySelector("main");
    const scrollable = mainElement || window;
    const currentScroll = mainElement ? mainElement.scrollTop : window.scrollY;
    const scrollHeight = mainElement
      ? mainElement.scrollHeight
      : document.documentElement.scrollHeight;
    const clientHeight = mainElement ? mainElement.clientHeight : window.innerHeight;

    if (currentScroll < 100) {
      const target = scrollHeight - clientHeight;
      scrollable === window
        ? window.scrollTo({ top: target, behavior: "smooth" })
        : scrollable.scrollTo({ top: target, behavior: "smooth" });
    } else {
      scrollable === window
        ? window.scrollTo({ top: 0, behavior: "smooth" })
        : scrollable.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // ✅ CFO Comment setter per page key
  const handleCfoCommentChange = (pageKey, value) => {
    setCfoCommentsMap((prev) => ({ ...prev, [pageKey]: value }));
  };

  const hasPages = filtersReady;
  const totalPages = stablePages.length;

  // ─────────────────────────────────────────────
  // Render helper — used in both main list & page viewer
  // ─────────────────────────────────────────────
  const renderPage = (pageData, isRefresh = false) => (
    <div data-pdf-section>
      <PageRenderer
        key={pageData.id}
        number={pageData.pageNumber}
        year={selectedYear}
        month={selectedMonth}
        monthNumber={selectedMonthNumber}
        contract={selectedContract}
        page={pageData.page}
        isExporting={isExporting || !!renderJob}
        isRefresh={isRefresh}
        lastTitleComment={lastTitleComment}
        onLastTitleCommentChange={setLastTitleComment}
        cfoComment={cfoCommentsMap[pageData.id] || ""}
        onCfoCommentChange={(value) => handleCfoCommentChange(pageData.id, value)}
      />
    </div>
  );

  // Render mode: every slide at native 1280px, one under the other, for the worker to capture
  if (renderJob) {
    return (
      <QueueContext.Provider value={{ enqueue }}>
        <div ref={contentRef} data-render-root style={{ width: 1280, background: "#fff" }}>
          {stablePages.map((pageData) => (
            <div
              key={pageData.id}
              style={{ width: 1280, minHeight: 720, background: "#fff", overflow: "hidden" }}
            >
              {renderPage(pageData)}
            </div>
          ))}
        </div>
      </QueueContext.Provider>
    );
  }

  return (
    <QueueContext.Provider value={{ enqueue }}>
      <div className="h-full flex flex-col min-h-0 px-4 sm:px-5 lg:px-6" ref={mainContainerRef}>
        {/* Controls */}
        <BoardPackControls
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          selectedContract={selectedContract}
          selectedBoardPackConfig={selectedBoardPackConfig}
          setSelectedBoardPackConfig={setSelectedBoardPackConfig}
          onYearChange={setSelectedYear}
          onMonthChange={setSelectedMonth}
          onContractChange={setSelectedContract}
          onExportPDF={() => requestBackgroundJob("export", "pdf")}
          onExportPPT={() => requestBackgroundJob("export", "pptx")}
          onRequestFinalize={handleRequestFinalize}
          isRequestingFinalize={isRequestingFinalize}
          boardPackJobs={boardPackJobs}
          onRefreshAll={handleRefreshAll}
          isRefreshingAll={isRefreshingAll}
          onConfigLoadingChange={setIsConfigLoading}
        />

        {/* Page Viewer Toggle Button */}
        {/* {hasPages && totalPages > 0 && (
          <div className="top-[64px] right-0 z-10 flex justify-end pr-6 pt-2 mb-4">
            <Button
              onClick={() => {
                setPageViewerOpen(true);
                setCurrentPageIndex(0);
              }}
              variant="outline"
              size="icon"
              className="h-10 w-10 rounded-full shadow-lg bg-background"
              title="Open Page Viewer"
            >
              <Maximize2 className="w-5 h-5" />
            </Button>
          </div>
        )} */}

        {/* Main Content */}
        <div
          ref={contentRef}
          className="flex-1 min-h-0 overflow-hidden"
          style={{ width: "100%", position: "relative" }}
        >
          {!hasPages ? (
            isConfigLoading ? (
              <LoadingComponent />
            ) : (
              <div className="flex flex-col items-center justify-center gap-6 min-h-[60vh] py-6">
                <Card className="w-full max-w-md text-center">
                  <CardHeader>
                    <CardTitle className="text-destructive flex items-center gap-2 text-center justify-center">
                      <AlertCircle className="w-5 h-5" />
                      <span>SELECT CONTRACT, YEAR & MONTH</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Please select all required fields to view the board pack
                    </p>
                  </CardContent>
                </Card>
                {/* Running / ready exports and finalizes of all board packs */}
                {/* <FinalizeRequestsPanel
                  title="Exports & finalizes"
                  className="w-full max-w-3xl"
                /> */}
              </div>
            )
          ) : (
            <div className="h-full">
              <SlideViewer>
                {stablePages.map((pageData) => (
                  <PageWithRefresh
                    key={pageData.id}
                    pageData={pageData}
                    selectedContract={selectedContract}
                    globalRefreshedPages={globalRefreshedPages}
                  >
                    {(refreshedPage, isRefresh) =>
                      renderPage(
                        {
                          ...pageData,
                          page: refreshedPage,
                        },
                        isRefresh
                      )
                    }
                  </PageWithRefresh>
                ))}
              </SlideViewer>
            </div>
          )}
        </div>

        {/* Full Screen Page Viewer */}
        {pageViewerOpen && totalPages > 0 && (
          <div className="fixed inset-0 z-[100] bg-background flex flex-col">
            <div className="flex-shrink-0 bg-background border-b px-6 py-4 flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-semibold">Page Viewer</h2>
                <span className="text-sm text-muted-foreground">
                  Page {currentPageIndex + 1} of {totalPages}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={goToPreviousPage}
                  disabled={currentPageIndex === 0}
                  variant="outline"
                  size="sm"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                </Button>
                <Button
                  onClick={goToNextPage}
                  disabled={currentPageIndex === totalPages - 1}
                  variant="outline"
                  size="sm"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                <Button
                  onClick={() => setPageViewerOpen(false)}
                  variant="ghost"
                  size="sm"
                  className="ml-2"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-background">
              {stablePages[currentPageIndex] && (
                <div className="w-full">{renderPage(stablePages[currentPageIndex])}</div>
              )}
            </div>
          </div>
        )}

        {/* Go to Page Button */}
        {/* {hasPages && !pageViewerOpen && (
          <div className="fixed bottom-[120px] right-6 z-[61] flex flex-col items-center gap-2">
            {goToPageOpen && (
              <div className="absolute bottom-[48px] right-0 flex items-center gap-1.5 bg-background border border-border rounded-lg px-2 py-1.5 shadow-md">
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={goToPageInput}
                  onChange={(e) => setGoToPageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleGoToPage();
                    if (e.key === "Escape") setGoToPageOpen(false);
                  }}
                  placeholder={`1-${totalPages}`}
                  className="w-16 text-sm bg-transparent outline-none border-none text-foreground"
                  autoFocus
                />
                <button
                  onClick={handleGoToPage}
                  className="text-xs px-2 py-1 rounded bg-muted hover:bg-muted/80 text-foreground cursor-pointer"
                >
                  Go
                </button>
              </div>
            )}
            <button
              onClick={() => setGoToPageOpen((prev) => !prev)}
              className="h-10 w-10 rounded-full shadow-lg bg-primary hover:bg-primary/90 text-white flex items-center justify-center cursor-pointer"
              title="Go to page"
              style={{ border: "none", outline: "none" }}
            >
              <Search className="w-6 h-6" />
            </button>
          </div>
        )} */}

        {/* Scroll Button */}
        {/* {hasPages && !pageViewerOpen && showScrollButton && (
          <button
            onClick={handleScrollClick}
            className="fixed bottom-2 right-6.5 mb-16 z-[60] h-10 w-10 rounded-full shadow-lg bg-primary hover:bg-primary/90 text-white transition-all hover:scale-110 flex items-center justify-center cursor-pointer"
            style={{ border: "none", outline: "none" }}
          >
            {scrollDirection === "up" ? (
              <ArrowUp className="w-6 h-6" />
            ) : (
              <ArrowDown className="w-6 h-6" />
            )}
          </button>
        )} */}
      </div>
    </QueueContext.Provider>
  );
};

export default BoardPack;

// ─────────────────────────────────────────────────────────────
// PageRenderer
// ─────────────────────────────────────────────────────────────
const PageRenderer = ({
  page,
  number,
  year,
  month,
  monthNumber,
  contract,
  isExporting = false,
  isRefresh = false,
  lastTitleComment,
  onLastTitleCommentChange,
  cfoComment,
  onCfoCommentChange,
}) => {
  const commonProps = { year, month, monthNumber, contract, page, isRefresh };
  switch (page?.type) {
    case "title_page":
      return (
        <BoardPackLayout isExporting={isExporting} page={page}>
          <PageUI page={page} />
          <TitlePage {...commonProps} />
        </BoardPackLayout>
      );
    case "main_table":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <MainTable
            {...commonProps}
            isExporting={isExporting}
            cfoComment={cfoComment}
            onCfoCommentChange={onCfoCommentChange}
          />
        </BoardPackLayout>
      );
    case "third_table":
      return (
        <BoardPackLayout isExporting={isExporting} page={page}>
          <PageUI page={page} />
          {/* ✅ cfoComment + onCfoCommentChange + isExporting pass ho raha hai */}
          <ThirdTable
            {...commonProps}
            isExporting={isExporting}
            cfoComment={cfoComment}
            onCfoCommentChange={onCfoCommentChange}
          />
        </BoardPackLayout>
      );
    case "large_table":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <LargeTable
            {...commonProps}
            isExporting={isExporting}
            cfoComment={cfoComment}
            onCfoCommentChange={onCfoCommentChange}
          />
        </BoardPackLayout>
      );
    case "kpi_enforcement_table":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <KpiEnforcementTable {...commonProps} />
        </BoardPackLayout>
      );
    case "bar_chart":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <BarChart {...commonProps} />
        </BoardPackLayout>
      );
    case "kpi_chart_12":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <KpiChart12 {...commonProps} />
        </BoardPackLayout>
      );
    case "Dotted_Line_Chart":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <PageOneChartContainer
            {...commonProps}
            isExporting={isExporting}
            cfoComment={cfoComment}
            onCfoCommentChange={onCfoCommentChange}
          />
        </BoardPackLayout>
      );
    case "new_chart_kpi":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <NewChartKPIPage {...commonProps} />
        </BoardPackLayout>
      );
    case "new_stack_chart":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <NewStackChartPage {...commonProps} />
        </BoardPackLayout>
      );
    case "new_chart_component":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <NewChartComponentPage {...commonProps} />
        </BoardPackLayout>
      );
    case "kpi_chart_12_new":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <KpiChart12NEW {...commonProps} />
        </BoardPackLayout>
      );
    case "kpi_chart_new":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <KpiChartNEW {...commonProps} />
        </BoardPackLayout>
      );
    case "kpi_chart_14":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <KpiChart14 {...commonProps} />
        </BoardPackLayout>
      );
    case "area_chart":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <AreaChartPage {...commonProps} />
        </BoardPackLayout>
      );
    case "kpi_chart_13":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <Kpi_Chart_13 {...commonProps} />
        </BoardPackLayout>
      );
    case "donut_chart_18":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <DonutChart18 {...commonProps} />
        </BoardPackLayout>
      );
    case "donut_chart":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <DonutChart {...commonProps} />
        </BoardPackLayout>
      );
    case "line_bars":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <LineBars {...commonProps} />
        </BoardPackLayout>
      );
    case "line_bars_table":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <Linebarstable {...commonProps} />
        </BoardPackLayout>
      );
    case "last_title":
      return (
        <BoardPackLayout isExporting={isExporting} page={page}>
          <PageUI page={page} />
          <LastTitle
            {...commonProps}
            hideInputField={isExporting}
            comment={lastTitleComment}
            onCommentChange={onLastTitleCommentChange}
          />
        </BoardPackLayout>
      );

    // Consolidate BoardPack
    case "cover_page":
      return (
        <CoverPage
          isSidebar={true}
          isFooter={true}
          isExporting={isExporting}
          page={page}
          year={year}
          month={month}
        />
      );
    case "toc_page":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={true}>
          <PageUI page={page} />
          <Toc_Page {...commonProps} />
        </BoardPackLayout>
      );
    case "pl_summary_page":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <PL_Summary_Page {...commonProps} />
        </BoardPackLayout>
      );

      case "P&L_Report":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <PL_Report {...commonProps} />
        </BoardPackLayout>
      );
      case "Financial_Table":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <Financial_Table {...commonProps} />
        </BoardPackLayout>
      );
      case "Mawgif_Group_Table":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <Mawgif_Group_Table {...commonProps} />
        </BoardPackLayout>
      );
      case "Business_Performance":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <Business_Performance_Page {...commonProps} />
        </BoardPackLayout>
      );
      case "Bussiness_Performance_By_Country":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <Bussiness_Performance_By_Country {...commonProps} />
        </BoardPackLayout>
      );
      case "Budget_Actual_Chart":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <Budget_Actual_Chart_page {...commonProps} />
        </BoardPackLayout>
      );
      case "Budget_Actual_Chart_V2":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <Budget_Actual_Chart_Page_v2 {...commonProps} />
        </BoardPackLayout>
      );
      case "Bridge_Summary_Page":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false}>
          <PageUI page={page} />
          <Bridge_Summary_Page {...commonProps} />
        </BoardPackLayout>
      );

    // -- Profiles Pages
    case "jeddah_profile_summary02":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <JeddahProfileSummary2 {...commonProps} />
        </BoardPackLayout>
      );
    case "jeddah_profile_summary10":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <JeddahProfileSummary11 {...commonProps} />
        </BoardPackLayout>
      );
    case "jeddah_profile_summary_arabic":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <Page27AirportEnforcementProfileTable {...commonProps} />
        </BoardPackLayout>
      );
    case "sharjha_profile_table":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <SharjahProfileSummary2 {...commonProps} />
        </BoardPackLayout>
      );
    case "profile_summary_airport_jordan":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <Jordan_2 {...commonProps} />
        </BoardPackLayout>
      );
    case "profile_summary_lovers_market":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <LoversMarket_2 {...commonProps} />
        </BoardPackLayout>
      );
    case "profile_summary_AlBatha_4":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <AlBatha_4 {...commonProps} />
        </BoardPackLayout>
      );
    case "profile_summary_AlBatha_11":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <AlBatha_11 {...commonProps} />
        </BoardPackLayout>
      );
    case "profile_summary_dammam":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <DammamAirport_2 {...commonProps} />
        </BoardPackLayout>
      );
    case "profile_summary_bilal":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <TotalBilal_4 {...commonProps} />
        </BoardPackLayout>
      );
    case "profile_summary_BabAlRee_4":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <BabAlRee_4 {...commonProps} />
        </BoardPackLayout>
      );
    case "profile_summary_BabAlRee_11":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <BabAlRee_11 {...commonProps} />
        </BoardPackLayout>
      );
    case "Gazzaz_4":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <Gazzaz_4 {...commonProps} />
        </BoardPackLayout>
      );
    case "Gazzaz_11":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <Gazzaz_11 {...commonProps} />
        </BoardPackLayout>
      );
    case "JedWatch_4":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <JedWatch_4 {...commonProps} />
        </BoardPackLayout>
      );
    case "JedWatch_11":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <JedWatch_11 {...commonProps} />
        </BoardPackLayout>
      );
    case "total_dubai_airport_profile":
    case "dubai_airport_t1_profile":
    case "dubai_airport_t2_profile":
    case "dubai_airport_t3_profile":
    case "dubai_airport_cargo_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <DubaiAirportProfile {...commonProps} />
        </BoardPackLayout>
      );
    case "madinah_haram_profile":
    case "madinah_haram_parking_profile":
    case "madinah_haram_enforcement_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <MadinahHaramProfile {...commonProps} />
        </BoardPackLayout>
      );
    case "taif_on_street_profile":
    case "taif_off_street_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <TaifProfile {...commonProps} />
        </BoardPackLayout>
      );
    case "khamis_on_street_profile":
    case "khamis_off_street_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <KhamisProfile {...commonProps} />
        </BoardPackLayout>
      );
    case "jubail_on_street_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <JubailProfile {...commonProps} />
        </BoardPackLayout>
      );
    case "alhassa_on_street_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <AlHassaProfile {...commonProps} />
        </BoardPackLayout>
      );
    case "mawgif_jeddah_on_street_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <MawgifJeddahProfile {...commonProps} />
        </BoardPackLayout>
      );
    case "modern_city_contract":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <ModernCity {...commonProps} />
        </BoardPackLayout>
      );
    case "digital_city_off_street_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <DigitalCityOffStreetProfile {...commonProps} />
        </BoardPackLayout>
      );
    case "digital_city_on_street_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <DigitalCityOnStreetProfile {...commonProps} />
        </BoardPackLayout>
      );

    case "albaha_on_street_profile":
    case "albaha_off_street_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <AlBahaProfile {...commonProps} />
        </BoardPackLayout>
      );
    case "dammam_causeway_on_street_profile":
    case "dammam_causeway_off_street_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <DammamCausewayProfile {...commonProps} />
        </BoardPackLayout>
      );

    case "baljurashi_on_street_profile":
    case "baljurashi_off_street_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <BaljurashiProfile {...commonProps} />
        </BoardPackLayout>
      );
    case "makkah_on_street_profile":
    case "makkah_off_street_profile":
    case "makkah_misfalah_profile":
    case "makkah_jarwal_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <MakkahProfile {...commonProps} />
        </BoardPackLayout>
      );
    case "jeddah_on_street_profile":
    case "jeddah_off_street_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <JeddahProfile {...commonProps} />
        </BoardPackLayout>
      );
    case "moc_on_street_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <MocProfile {...commonProps} />
        </BoardPackLayout>
      );
    case "capital_parking_on_street_profile":
      return (
        <BoardPackLayout isExporting={isExporting} page={page} isSidebar={false} isFooter={false}>
          <PageUI page={page} />
          <CapitalParkingOnStreet {...commonProps} />
        </BoardPackLayout>
      );
  }
};

// const PageUI = ({ page }) => <>{page?.type}</>
const PageUI = ({ page }) => <></>;
const PageNumber = ({ pageData }) => (
  <>
    <div className="flex items-center justify-items-start">
      <span className="text-md font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full shadow-sm ">
        Page No. {pageData?.page?.page_no || pageData?.pageNumber}
      </span>
    </div>
  </>
);

const PageWithRefresh = ({ children, pageData, selectedContract, globalRefreshedPages = null }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshedPage, setRefreshedPage] = useState(pageData.page);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hasRefreshed, setHasRefreshed] = useState(false);

  // Per-page manual refresh — calls API itself
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await apiCall("get", "/boardpack/config/get", {
        contract: selectedContract,
      });

      if (response?.success && response?.data?.config?.pages) {
        const updatedPageConfig = response.data.config.pages.find(
          (p) => p.page_no === pageData.page?.page_no && p.type === pageData.page?.type
        );
        if (updatedPageConfig) {
          setRefreshedPage(updatedPageConfig);
          setRefreshKey((prev) => prev + 1);
          setHasRefreshed(true);
        }
      }
    } catch (err) {
      console.error("Refresh failed:", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Global refresh — parent already fetched once, just pick our page from the result
  useEffect(() => {
    if (!globalRefreshedPages) return;
    const updatedPageConfig = globalRefreshedPages.find(
      (p) => p.page_no === pageData.page?.page_no && p.type === pageData.page?.type
    );
    if (updatedPageConfig) {
      setTimeout(() => {
        setRefreshedPage(updatedPageConfig);
        setRefreshKey((prev) => prev + 1);
        setHasRefreshed(true);
      }, 0);
    }
  }, [globalRefreshedPages]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="flex items-end justify-end mb-2 mt-1">
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-3 py-1 rounded-md transition-colors disabled:opacity-50 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200"
        >
          {isRefreshing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          <span>Refresh</span>
        </button>
      </div>
      <div key={refreshKey}>{children(refreshedPage, hasRefreshed)}</div>
    </>
  );
};